import SockJS from "sockjs-client";
import { Client } from "@stomp/stompjs";
import { getToken } from "functions/Auth";

let client = null;
let connected = false;
let subscriptions = new Map(); // topic -> callback
let isActivating = false;

export const stompService = {
  /**
   * Connect to STOMP WebSocket
   * Auto-reconnect enabled
   */
  connect: (url, onConnect, onError) => {
    if (client?.active || isActivating) {
      return client;
    }

    isActivating = true;
    const token = getToken();

    client = new Client({
      webSocketFactory: () =>
        new SockJS(`${url}?token=${token}`),

      connectHeaders: {
        Authorization: `Bearer ${token}`,
      },

      // 🔁 Auto reconnect every 5s
      reconnectDelay: 5000,

      // ❤️ Heartbeats
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,

      debug: () => {}, // silence logs in prod

      onConnect: (frame) => {
        connected = true;
        isActivating = false;
        console.log("✅ STOMP connected");

        // 🔄 Restore subscriptions after reconnect
        subscriptions.forEach((callback, topic) => {
          client.subscribe(topic, callback);
        });

        onConnect?.(frame);
      },

      onStompError: (frame) => {
        console.error("❌ STOMP broker error:", frame);
        connected = false;
        onError?.(frame);
      },

      onWebSocketClose: () => {
        console.warn("🔌 WebSocket closed");
        connected = false;
      },

      onWebSocketError: (err) => {
        console.error("❌ WebSocket error", err);
        connected = false;
      },
    });

    client.activate();
    return client;
  },

  /**
   * Subscribe to topic
   */
  subscribe: (topic, callback) => {
    if (!client || !connected) {
      console.warn("⚠️ STOMP not connected yet");
      return null;
    }

    const wrappedCallback = (message) => {
      try {
        callback(JSON.parse(message.body), message);
      } catch {
        callback(message.body, message);
      }
    };

    const subscription = client.subscribe(topic, wrappedCallback);

    // store for reconnect
    subscriptions.set(topic, wrappedCallback);

    return () => {
      subscription.unsubscribe();
      subscriptions.delete(topic);
      console.log(`🧹 Unsubscribed from ${topic}`);
    };
  },

  /**
   * Send message
   */
  send: (destination, body = {}) => {
    if (!client || !connected) {
      console.warn("⚠️ Cannot send — not connected");
      return;
    }

    client.publish({
      destination,
      body: typeof body === "string" ? body : JSON.stringify(body),
    });
  },

  /**
   * Disconnect and cleanup
   */
  disconnect: () => {
    if (client) {
      client.deactivate();
      client = null;
      connected = false;
      isActivating = false;
      subscriptions.clear();
      console.log("🔌 STOMP disconnected");
    }
  },

  /**
   * Connection state
   */
  isConnected: () => connected,
};
